import ApiRequestManagerInstance from '../ApiRequestManager';
import CategoryUtils from '../../utils/categories';
import Rail from '../../classes/rail';
import Recording from '../../classes/record';
import RecordingType from '../../types/enums/recording';
import TranslationManager from '../../assets/locale/translationsManager';
import initRecordings from '../../actions/recordings/initRecordings';
import { store } from '../../store/store';
import { IRecordData, IRecordsRequest } from '../../types/interfaces/rail';
import { RailType } from '../../types/enums/rail';
import {
  addRecording,
  removeRecording,
} from '../../store/slices/recordings.slice';
import {
  IRecordIdentifier,
  IRecordIdentifierData,
  IRecordIdentifierRequest,
} from '../../types/interfaces/record';
import RecordType from '@shared/types/enums/recordType';
import HTTP_ERRORS from '@shared/types/enums/httpErrors';

const RECORDING_ERRORS = {
  ALREADY_CREATED: "User Recording previously created",
}

const RECORDING_ERROR_CODES = {
  UPGRADE_PLAN_REQUIRED: '201',
}


function addRecordingRequest(code: string, type: number) {
  const addRecord = (resolve: (status:boolean)=>void) => {
    initRecordings(false, false, true);
    store.dispatch(addRecording(new Recording(code, RecordingType.Pending)));
    resolve(true);
  }
  const promise = new Promise<boolean>(async (resolve, reject) => {
    try {
      const asset = `/${code}/${type}`;
      await ApiRequestManagerInstance.post({
        endpoint: {
          url: 'apiUrl',
          version: 'v4',
          section: 'media',
          subsection: 'record',
          params: asset,
        },
      });
      addRecord(resolve)
      identifierRecordingsRequest()
    } catch (e) {
      const {response} = e as {response: {status?: number, data: {error_code?: string, msg?: string}}};
      if(response?.status === HTTP_ERRORS.FORBBIDEN && response?.data?.error_code == RECORDING_ERROR_CODES.UPGRADE_PLAN_REQUIRED) {
        reject({
          error_code: response.data.error_code || RECORDING_ERROR_CODES.UPGRADE_PLAN_REQUIRED,
          msg: response.data.msg
        });
        return;
      }
      if(response?.data?.msg) {
        if(response?.data?.msg === RECORDING_ERRORS.ALREADY_CREATED) {
          if(type === RecordType.SEASON) {
            addRecordingRequest(code, RecordType.SHOW)
          }else {
            addRecord(resolve)
          }
          return 
        }
      }
      console.error(e);
      reject({});
    }
  });

  return promise;
}

function finishedRecordingsRequest() {
  const promise = new Promise<Rail[]>(async (resolve, reject) => {
    try {
      const response: IRecordsRequest = (await ApiRequestManagerInstance.get({
        endpoint: {
          url: 'apiUrl',
          version: 'v6',
          section: 'user',
          subsection: 'records',
        },
      })) as unknown as IRecordsRequest;
      const rails = (response.data as IRecordData[]).map((item) => {
        const category = CategoryUtils.getCategoryById(item.category);
        const rail = {
          section: {
            title: category?.title,
            presentationType: category?.presentationType,
            // Suffixed so a category with both finished and pending
            // recordings doesn't produce two rails sharing the same id.
            name: `${category?.id || item.category}-finished`,
          },
          shows: { data: item.shows },
        };
        return new Rail(rail, null, RailType.RECORDING);
      }, []);
      resolve(rails);
    } catch (e) {
      console.error(e);
      reject([]);
    }
  });

  return promise;
}

function identifierRecordingsRequest() {
  const promise = new Promise<IRecordIdentifier[]>(async (resolve, reject) => {
    try {
      const response: IRecordIdentifierRequest =
        (await ApiRequestManagerInstance.get({
          endpoint: {
            url: 'apiUrl',
            version: 'v4',
            section: 'user',
            subsection: 'recordingsIds',
          },
        })) as unknown as IRecordIdentifierRequest;
      let identifiers: IRecordIdentifier[] = [];
      const data = response.data as IRecordIdentifierData[];
      data.forEach((recording) => {
        identifiers = identifiers.concat({
          code: recording.event_code,
          deleteId: recording.id,
          type: recording.type,
        });
      });
      resolve(identifiers);
    } catch (e) {
      console.error(e);
      reject([]);
    }
  });

  return promise;
}

function pendingRecordingsRequest() {
  const promise = new Promise<Rail[]>(async (resolve, reject) => {
    try {
      const response: IRecordsRequest = (await ApiRequestManagerInstance.get({
        endpoint: {
          url: 'apiUrl',
          version: 'v5',
          section: 'user',
          subsection: 'pendingRecords',
        },
      })) as unknown as IRecordsRequest;
      const rails = response.data.map((item) => {
        const category = CategoryUtils.getCategoryById(item.category);
        const rail = {
          section: {
            title: TranslationManager.get('PAGES.RECORDINGS.PENDING'),
            presentationType: category?.presentationType,
            // Suffixed so a category with both pending and finished
            // recordings doesn't produce two rails sharing the same id.
            name: `${category?.id || item.category}-pending`,
          },
          shows: { data: item.shows },
        };
        return new Rail(rail, null, RailType.RECORDING);
      }, []);
      resolve(rails);
    } catch (e) {
      console.error(e);
      reject([]);
    }
  });

  return promise;
}

function removeRecordingRequest(
  deleteId: string,
  pending: boolean,
  code: string
) {
  const promise = new Promise<boolean>(async (resolve, reject) => {
    try {
      const asset = `/${deleteId}`;
      await ApiRequestManagerInstance.delete({
        endpoint: {
          url: 'apiUrl',
          version: 'v4',
          section: 'media',
          subsection: pending ? 'cancelRecording' : 'record',
          params: asset,
        },
      });
      resolve(true);
    } catch (e) {
      console.error(e);
      reject(false);
    }
    store.dispatch(removeRecording(new Recording(code)));
  });

  return promise;
}

export {
  addRecordingRequest,
  finishedRecordingsRequest,
  identifierRecordingsRequest,
  pendingRecordingsRequest,
  removeRecordingRequest,
};
